import {
  Box,
  Grid2,
  Stack,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  CircularProgress,
} from "@mui/material";

import { IconRefresh } from "@tabler/icons-react";
import { FC, useState, useRef, useCallback, useEffect } from "react";
import {
  AdvancedMarker,
  APIProvider,
  Map,
  useMap,
  InfoWindow,
  useAdvancedMarkerRef,
  AdvancedMarkerProps,
} from "@vis.gl/react-google-maps";
import { Polyline } from "../../../components/polyline"; // import from your local file

import type { Marker } from "@googlemaps/markerclusterer";
import { GET } from "../../../request/service";
import { getDeviceListAndGNSSData } from "../../../request/api";
import dayjs, { Dayjs } from "dayjs";
import Badge from "@mui/material/Badge";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";
import { NO_DATA, REPORT_TITLE } from "../../../constants/dashboard";
import start from "../../../assets/images/start.png";
import end from "../../../assets/images/end.png";
import React from "react";

type Poi = { key: string; location: google.maps.LatLngLiteral; title: string };

interface Dashboard {
  DashboardId: string;
  Name: string;
}

interface Device {
  DeviceId: string;
  Name: string;
}

interface GnssData {
  DeviceId: string;
  Timestamp: string;
  Latitude: string;
  Longitude: string;
  Velocity: string;
}

interface PathPoint {
  lat: number;
  lng: number;
}

export type MapConfig = {
  id: string;
  mapId: string;
  defaultZoom: number;
  defaultCenter: PathPoint;
};

function ServerDay(
  props: PickersDayProps<Dayjs> & { highlightedDays?: number[] },
) {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

  const isSelected =
    !props.outsideCurrentMonth &&
    highlightedDays.indexOf(props.day.date()) >= 0;

  return (
    <Badge
      key={props.day.toString()}
      overlap="circular"
      badgeContent={isSelected ? "✅" : undefined}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Badge>
  );
}

const GnssPage: FC = () => {
  const [index, setIndex] = useState("0");

  const [dashboardId, setDashboardId] = useState("");

  const [dashboards, setDashboards] = useState<Dashboard[]>([]);

  const [deviceId, setDeviceId] = useState("");

  const [deviceName, setDeviceName] = useState("");

  const [devices, setDevices] = useState<Device[]>([]);

  const [dateValue, setDateValue] = useState<Dayjs | null>(null);

  const [gnssData, setGnssData] = useState<GnssData[]>([]);

  const [locationData, setLocationData] = useState<Poi[]>([]);

  const [position, setPosition] = useState({
    lat: 35.62467758521719,
    lng: 139.73701800945008,
  });

  const [locationPath, setLocationPath] = useState<PathPoint[]>([]);

  const [infowindowOpen, setInfowindowOpen] = useState(false);

  const [selectedMarker, setSelectedMarker] = useState<Marker | null>(null);

  const [selectedPoi, setSelectedPoi] = useState<Poi | null>(null);

  const [optionWidth, setOptionWidth] = useState("");

  const requestAbortController = useRef<AbortController | null>(null);
  const [highlightedDays, setHighlightedDays] = useState<number[]>([]);

  const [loading, setLoading] = useState(false);

  const [showGnssData, setShowGnssData] = useState(false);

  const componentRef = useRef<HTMLDivElement | null>(null);

  const [windowSize, setWindowSize] = useState({
    height: window.innerHeight,
  });

  const localDashboardList = JSON.parse(
    localStorage.getItem("dashboardList") || "[]",
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window.innerHeight]);

  const map = useMap("demoappMapId");

  useEffect(() => {
    if (!map) return;
    map.setZoom(7);
    // do something with the map instance
    console.log(map);
    console.log("useEffect map");
  }, [map]);

  useEffect(() => {
    let autoWidth = 0;
    if (localDashboardList && localDashboardList.length > 0) {
      localDashboardList.forEach((dashboard: Dashboard) => {
        if (dashboard.Name.length > autoWidth) {
          autoWidth = dashboard.Name.length;
        }
      });
      setDashboards(localDashboardList);
      setDashboardId(localDashboardList[0].DashboardId);
    }
    setOptionWidth(autoWidth * 9.5 + "px");
    selectChange(
      { target: { value: localDashboardList[0].DashboardId } },
      true,
    );
  }, []);

  useEffect(() => {
    if (componentRef.current && !showGnssData) {
      const rect = componentRef.current.getBoundingClientRect();
      setWindowSize({
        height: window.innerHeight - parseInt(rect.top.toString(), 10) - 30,
      });
    }
  }, [
    document.getElementById("no-data")?.innerHTML,
    window.innerHeight,
    gnssData,
  ]);

  useEffect(() => {
    const dom = document.getElementById("dashboardContainer");
    if (dom && showGnssData) {
      setWindowSize({
        height:
          window.innerHeight -
          parseInt(dom.getBoundingClientRect().top.toString(), 10) -
          95,
      });
    }
  }, [
    document.getElementById("dashboardContainer"),
    window.innerHeight,
    gnssData,
  ]);

  const onMarkerClick = useCallback(
    (poi: any, marker?: google.maps.marker.AdvancedMarkerElement) => {
      console.log(marker);
      console.log(poi);

      if (poi) {
        setSelectedPoi(poi);
      }

      if (marker) {
        setSelectedMarker(marker);
      }
      setInfowindowOpen(true);
    },
    [],
  );

  const selectChange = async (
    event: { target: { value: any } },
    firstLogin: boolean = false,
  ) => {
    setGnssData([]);
    setDevices([]);
    setDeviceId("");
    setShowGnssData(false);
    setInfowindowOpen(false);
    setDateValue(dayjs());
    setLoading(true);
    let localId = event.target.value;

    if (firstLogin) {
      localId = localDashboardList[0].DashboardId;
    }

    setDashboardId(localId);

    await getDeviceList(localId).then(async (deviceData: any) => {
      console.log("deviceData", deviceData);
      if (deviceData && deviceData.deviceList.length > 0) {
        setDevices(deviceData.deviceList);
        setDeviceId(deviceData.deviceList[0].DeviceId);
        setDeviceName(deviceData.deviceList[0].Name);
        await getGNSSRelatedData(
          deviceData.deviceList[0].DeviceId,
          dayjs(),
          localId,
        );
      } else {
        setShowGnssData(false);
      }
      setLoading(false);
    });
  };

  const getDeviceList = async (id: string) => {
    return await GET(
      getDeviceListAndGNSSData + "?dashboardId=" + id + "&date=&deviceId=",
      "",
    );
  };

  const getGNSSListData = async (
    id: string,
    type: string,
    date?: Dayjs,
    localDashboardId?: string,
  ) => {
    if (!deviceId && !id) {
      console.log("Please select Device!");
      return "";
    } else {
      type === "gnss" ? setLoading(true) : "";
      const localDate = date ? date : dateValue ? dateValue : dayjs();
      const localDashBoardId = localDashboardId
        ? localDashboardId
        : dashboardId
          ? dashboardId
          : localDashboardList[0].DashboardId;
      console.log(localDate.format("YYYY-MM-DD"));
      const dataFormat =
        type === "gnss"
          ? localDate.format("YYYY-MM-DD")
          : localDate.format("YYYY-MM");
      const localDeviceId = id ? id : deviceId;

      return await GET(
        getDeviceListAndGNSSData +
          "?dashboardId=" +
          localDashBoardId +
          "&date=" +
          dataFormat +
          "&deviceId=" +
          localDeviceId,
        "",
      );
    }
  };

  const getGNSSRelatedData = async (
    id: string,
    date?: Dayjs,
    dashboardId?: string,
  ) => {
    await Promise.all([
      getGNSSListData(id, "gnss", date, dashboardId),
      getGNSSListData(id, "highLight"),
    ]).then(([gnssRes, dateRes]: any) => {
      if (gnssRes) {
        console.log("res", gnssRes);
        const gnssDataObjects = gnssRes.gnssData;

        if (gnssDataObjects && gnssDataObjects.length > 0) {
          gnssDataObjects.forEach(
            (dataObj: { Velocity: string | undefined }) => {
              if (dataObj.Velocity === undefined) {
                dataObj.Velocity = "";
              }
            },
          );
          setGnssData(gnssDataObjects);
          convertGnssDataToPoi(gnssDataObjects);
          setShowGnssData(true);
          refreshClick();
        }
      } else {
        setGnssData([]);
        setShowGnssData(false);
      }

      if (dateRes) {
        console.log("res", dateRes);
        let daysToHighlight: number[] = [];
        const dateDataObjects = dateRes.dateData;
        const controller = new AbortController();

        if (dateDataObjects && dateDataObjects.length > 0) {
          daysToHighlight = dateDataObjects.map((day: string) =>
            parseInt(day, 10),
          );
          console.log(daysToHighlight);

          setHighlightedDays(daysToHighlight);
          requestAbortController.current = controller;
        }
      }
      setLoading(false);
    });
  };

  const selectDeviceChange = async (event: { target: { value: any } }) => {
    setShowGnssData(false);
    setInfowindowOpen(false);
    setLoading(true);
    setDeviceId(event.target.value);
    setDateValue(dayjs());
    const name =
      devices.find((device) => device.DeviceId === event.target.value)?.Name ??
      "";
    setDeviceName(name);
    console.log(`selectDeviceName: ${name}`);

    await getGNSSRelatedData(event.target.value, dayjs()).then(() => {
      setLoading(false);
    });
  };

  const queryClick = () => {
    console.log(map);
    console.log(deviceId);
    console.log(dashboardId);
    setInfowindowOpen(false);
    setGnssData([]);
    let dataFormat;

    if (!dashboardId) {
      console.log("Please select Dashboard!");
    } else if (!deviceId) {
      console.log("Please select Device!");
    } else if (dateValue) {
      console.log(dateValue.format("YYYY-MM-DD"));
      dataFormat = dateValue.format("YYYY-MM-DD");

      GET(
        getDeviceListAndGNSSData +
          "?dashboardId=" +
          dashboardId +
          "&date=" +
          dataFormat +
          "&deviceId=" +
          deviceId,
        "",
      ).then((res: any) => {
        if (res) {
          const gnssDataObjects = res.gnssData;
          gnssDataObjects.forEach(
            (dataObj: { Velocity: string | undefined }) => {
              if (dataObj.Velocity === undefined) {
                dataObj.Velocity = "";
              }
            },
          );
          setShowGnssData(true);
          setGnssData(gnssDataObjects);
          convertGnssDataToPoi(gnssDataObjects);
          refreshClick();
        } else {
          setGnssData([]);

          console.log(gnssData);
          setShowGnssData(false);
        }
      });
    } else {
      console.log("query error!");
    }
  };

  const loadingHighlightDate = async (flag?: any, date?: Dayjs) => {
    let daysToHighlight: number[] = [];
    setHighlightedDays([]);
    const timer = setTimeout(async () => {
      getGNSSListData(deviceId, "highLight", date).then((res: any) => {
        console.log(res);
        if (res) {
          const dateData = res.dateData;

          if (dateData && dateData.length > 0) {
            daysToHighlight = dateData.map((day: string) => parseInt(day, 10));
            setHighlightedDays(daysToHighlight);
          }
        }
      });
    }, 200);

    if (flag && flag.signal) {
      flag.signal.onabort = () => {
        clearTimeout(timer);
      };
    }
  };

  const refreshClick = () => {
    setIndex(index + 1);
  };

  const convertGnssDataToPoi = (gnssDataObjects: any) => {
    console.log(gnssData);
    const locations: Poi[] = [];
    const locationpaths: PathPoint[] = [];

    gnssDataObjects.forEach((dataObj: GnssData, index: number) => {
      const poi: Poi = {
        key: "" + (1 + index),
        location: {
          lat: parseFloat(dataObj.Latitude),
          lng: parseFloat(dataObj.Longitude),
        },
        title: `
        DeviceID: ${dataObj.DeviceId} <br>
        DeviceName: ${deviceName} <br>
        Timestamp: ${dataObj.Timestamp} <br>
        Velocity: ${dataObj.Velocity && Number(dataObj.Velocity) > 0 ? Number(dataObj.Velocity).toFixed(2) : 0} (m/s) <br>
        Latitude: ${parseFloat(dataObj.Latitude)} <br>
        Longitude: ${parseFloat(dataObj.Longitude)}`,
      };

      const pathPoint: PathPoint = {
        lat: parseFloat(dataObj.Latitude),
        lng: parseFloat(dataObj.Longitude),
      };

      console.log(poi);
      locations.push(poi);
      locationpaths.push(pathPoint);
    });

    setPosition({
      lat: parseFloat(gnssDataObjects[0].Latitude),
      lng: parseFloat(gnssDataObjects[0].Longitude),
    });
    setLocationData(locations);

    setLocationPath(locationpaths);
    console.log(position);
  };

  const handleMonthChange = (date: Dayjs) => {
    const controller = new AbortController();
    if (requestAbortController.current) {
      requestAbortController.current.abort();
    }

    setInfowindowOpen(false);
    setHighlightedDays([]);
    // setDateValue(date);
    loadingHighlightDate(
      {
        signal: controller.signal,
      },
      date,
    );
  };

  const BuildInfoWindow = () => {
    try {
      return infowindowOpen ? (
        <InfoWindow
            anchor={selectedMarker}
            headerContent={
              <h3
                style={{
                  margin: 0,
                }}
              >
                Gnss Info
              </h3>
            }
            maxWidth={270}
            onCloseClick={() => setInfowindowOpen(false)}
          >
            <p
              style={{ marginTop: "0px" }}
              dangerouslySetInnerHTML={{
                __html: selectedPoi?.title ?? "",
              }}
            ></p>
          </InfoWindow>
      ) : "";
    } catch (e) {
      console.log("buildInfoWindow error: ", e);
      return "";
    }
  };

  return (
    <>
      {optionWidth && (
        <Box marginLeft={"-10px"}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              component="h5"
              fontSize="1.3rem"
              fontWeight={400}
              marginLeft={"10px"}
              marginBottom={"5px"}
              color="rgba(0, 0, 0, 0.6)"
            >
              {REPORT_TITLE.GNSS}
            </Typography>
          </Stack>

          <Grid2
            container
            spacing={2}
            columns={10}
            alignItems="center"
            id="dashboardContainer"
          >
            <Grid2>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  paddingBottom: "10px",
                  paddingLeft: "10px",
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">
                    Dashboards
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={dashboardId}
                    label="Dashboards"
                    onChange={(event) => selectChange(event)}
                    sx={{ minWidth: optionWidth }}
                  >
                    {dashboards.map((dashboard) => (
                      <MenuItem
                        key={dashboard.DashboardId}
                        value={dashboard.DashboardId}
                      >
                        {dashboard.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Grid2>

            <Grid2>
              <Stack
                direction="row"
                spacing={3}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "flex-end",
                  paddingBottom: "10px",
                  display: "flex",
                  flexWrap: "wrap",
                  marginLeft: "8px",
                }}
                flexGrow={1}
              >
                <FormControl
                  sx={{
                    minWidth: "248px",
                    ".css-16p630n-MuiInputBase-root-MuiFilledInput-root-MuiSelect-root.Mui-disabled":
                      {
                        borderRadius: "4px",
                        ":before": {
                          borderBottomStyle: "unset",
                        },
                      },
                  }}
                >
                  <InputLabel id="demo-simple-select-label">Devices</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={deviceId}
                    label="Devices"
                    variant={devices.length === 0 ? "filled" : "outlined"}
                    disabled={devices.length === 0}
                    onChange={(event) => selectDeviceChange(event)}
                  >
                    {devices.map((device) => (
                      <MenuItem key={device.DeviceId} value={device.DeviceId}>
                        {device.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{
                      marginLeft: "10px",
                    }}
                    label="Date"
                    defaultValue={dayjs()}
                    value={dateValue}
                    onOpen={() => loadingHighlightDate()}
                    // onClose={() => {
                    //   fetchHighlightedDays(null, "");
                    // }}
                    onChange={(newValue) => {
                      setDateValue(newValue);
                      setInfowindowOpen(false);
                      getGNSSListData(
                        deviceId,
                        "gnss",
                        newValue ?? undefined,
                      ).then((gnssRes: any) => {
                        console.log("res", gnssRes);
                        const gnssDataObjects = gnssRes.gnssData;

                        if (gnssDataObjects && gnssDataObjects.length > 0) {
                          gnssDataObjects.forEach(
                            (dataObj: { Velocity: string | undefined }) => {
                              if (dataObj.Velocity === undefined) {
                                dataObj.Velocity = "";
                              }
                            },
                          );
                          setGnssData(gnssDataObjects);
                          convertGnssDataToPoi(gnssDataObjects);
                          setShowGnssData(true);
                          refreshClick();
                        } else {
                          setGnssData([]);
                          setShowGnssData(false);
                        }
                        setLoading(false);
                      });
                    }}
                    onMonthChange={(event) => handleMonthChange(event)}
                    renderLoading={() => <DayCalendarSkeleton />}
                    slots={{
                      day: ServerDay,
                    }}
                    slotProps={{
                      day: {
                        highlightedDays,
                      } as any,
                    }}
                  />
                </LocalizationProvider>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={queryClick}
                  sx={{
                    borderRadius: "50%",
                    padding: "12px",
                    minWidth: "auto",
                    height: "auto",
                    width: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconRefresh stroke={1.5} />
                </Button>
              </Stack>
            </Grid2>
          </Grid2>
          <Grid2 container spacing={2} columns={16}></Grid2>
          <Grid2 container spacing={2} columns={16}>
            {loading ? (
              <>
                <Box
                  sx={{
                    height: "78vh",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Fade
                    in={loading}
                    style={{
                      transitionDelay: loading ? "400ms" : "0ms",
                    }}
                    unmountOnExit
                  >
                    <CircularProgress />
                  </Fade>
                </Box>
              </>
            ) : (
              <>
                {showGnssData && (
                  <>
                    <div
                      style={{
                        height: `${windowSize.height}px`,
                        width: "100%",
                        marginLeft: "10px",
                        position: "relative",
                      }}
                    >
                      <APIProvider
                        apiKey={"AIzaSyAYgk0WC6f03TvStuV4fFM5-Fg7rGTIhVI"}
                      >
                        <Map
                          defaultCenter={position}
                          defaultZoom={13}
                          mapId="demoapp-map"
                          id={index}
                        >
                          {locationData.map((poi: Poi, index: number) => (
                            <AdvancedMarkerWithRef
                              onMarkerClick={(
                                marker: google.maps.marker.AdvancedMarkerElement,
                              ) => onMarkerClick(poi, marker)}
                              position={poi.location}
                            >
                              {index === 0 && (
                                <img
                                  src={start}
                                  style={{
                                    width: "50px",
                                    marginBottom: "-17px",
                                    position: "relative",
                                    zIndex: 1000,
                                  }}
                                  alt="Start"
                                />
                              )}
                              {index === locationData.length - 1 && index > 0 && (
                                <img
                                  src={end}
                                  style={{
                                    width: "50px",
                                    marginBottom: "-17px",
                                    zIndex: 1000,
                                    position: "relative",
                                  }}
                                  alt="End"
                                />
                              )}

                              <div
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  backgroundColor: "#d7d7d5",
                                  borderRadius: "50%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  color: "white",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  border: "1px solid darkgray",
                                  marginLeft:
                                    index === 0 ||
                                    index === locationData.length - 1
                                      ? "19px"
                                      : "0px",
                                  marginBottom: "-5px",
                                  zIndex: 99,
                                  position: "relative",
                                }}
                              />
                            </AdvancedMarkerWithRef>
                          ))}
                          <BuildInfoWindow />

                          <Polyline
                            path={locationPath}
                            strokeWeight={8}
                            strokeColor={"#467b61"}
                            strokeOpacity={1}
                          />

                          <Polyline
                            path={locationPath}
                            strokeWeight={4}
                            strokeColor={"#9debbd"}
                            strokeOpacity={1}
                          />
                        </Map>
                      </APIProvider>
                    </div>
                  </>
                )}
                {!showGnssData && !loading && (
                  <Grid2
                    ref={componentRef}
                    id={"no-data"}
                    sx={{
                      marginTop: "10px",
                      height: `${windowSize.height}px`,
                      width: "100%",
                      display: "grid",
                      alignItems: "center",
                      justifyItems: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      color: "rgba(0, 0, 0, 0.4)",
                      fontSize: 40,
                      marginLeft: "10px",
                    }}
                    container
                    spacing={2}
                    columns={16}
                  >
                    {NO_DATA.MESSAGE}
                  </Grid2>
                )}
              </>
            )}
          </Grid2>
        </Box>
      )}
    </>
  );
};

export const AdvancedMarkerWithRef = (
  props: AdvancedMarkerProps & {
    onMarkerClick: (marker: google.maps.marker.AdvancedMarkerElement) => void;
  },
) => {
  const { children, onMarkerClick, ...advancedMarkerProps } = props;
  const [markerRef, marker] = useAdvancedMarkerRef();

  return (
    <AdvancedMarker
      onClick={() => {
        if (marker) {
          onMarkerClick(marker);
        }
      }}
      ref={markerRef}
      {...advancedMarkerProps}
    >
      {children}
    </AdvancedMarker>
  );
};

export default GnssPage;
